import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@services/api";
import { orderKeys, useSlotOrderCountQuery } from "@queries/order";
import { useCustomerAddressesQuery } from "@queries/customers/addresses";
import { buildGoogleMapsUrl } from "@lib/coordinates";
import "./css/order-addresses.css";

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "");
const formatAddressLabel = (address) =>
  [
    address?.address,
    address?.building_number ? `Building Number: ${address.building_number}` : null,
    address?.floor ? `Floor: ${address.floor}` : null,
    address?.comment ? `Comment: ${address.comment}` : null,
    address?.area ? `Area: ${address.area}` : null,
    address?.type,
  ]
    .filter(Boolean)
    .join(", ");
const normalizeAddressId = (value) => {
  if (value === undefined || value === null) return "";
  const stringValue = String(value);
  return stringValue === "0" ? "" : stringValue;
};

function SlotSelect({ value, options = [], onChange, disabled }) {
  return (
    <select
      className="oa-select"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">--</option>
      {options.map((s) => (
        <option key={s.presentation} value={s.presentation}>
          {s.presentation}
        </option>
      ))}
    </select>
  );
}

function SlotOrderCount({ date, timeSlot }) {
  const { data: count, isFetching } = useSlotOrderCountQuery(date, timeSlot);
  if (!date || !timeSlot) return null;
  const over = !isFetching && (count ?? 0) > 55;
  return (
    <span style={{
      marginLeft: 8,
      fontSize: 15,
      fontWeight: over ? 700 : 500,
      color: over ? "#fff" : "#333",
      background: over ? "#dc2626" : "#e5e7eb",
      borderRadius: 5,
      padding: "3px 10px",
      display: "inline-block",
    }}>
      {isFetching ? "…" : `Time Slot Capacity: ${count ?? 0} orders`}
    </span>
  );
}

export default function OrderAddresses({ order }) {
  const id = order?.order_id;
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: orderKeys.one(id) });

  const { data: addressesRes } = useCustomerAddressesQuery(order?.customer_id);
  const addresses = addressesRes?.data || [];

  const [editPickup, setEditPickup] = React.useState(false);
  const [editDropoff, setEditDropoff] = React.useState(false);

  const [pickup, setPickup] = React.useState({
    address_id: normalizeAddressId(order?.pick_up_address_id),
    address: order?.pickup_address || order?.pickup?.address || "",
    date: fmtDate(order?.pickup_date) || "",
    time_slot: order?.pickup_time_slot || order?.pickup?.time_slot || "",
  });
  const [dropoff, setDropoff] = React.useState({
    address_id: normalizeAddressId(order?.drop_off_address_id),
    address: order?.dropoff_address || order?.dropoff?.address || "",
    date: fmtDate(order?.dropoff_date) || "",
    time_slot: order?.dropoff_time_slot || order?.dropoff?.time_slot || "",
  });

  // Keep local state in sync when `order` changes (e.g. after refetch)
  React.useEffect(() => {
    setPickup({
      address_id: normalizeAddressId(order?.pick_up_address_id),
      address: order?.pickup_address || order?.pickup?.address || "",
      date: fmtDate(order?.pickup_date) || "",
      time_slot: order?.pickup_time_slot || order?.pickup?.time_slot || "",
    });
    setDropoff({
      address_id: normalizeAddressId(order?.drop_off_address_id),
      address: order?.dropoff_address || order?.dropoff?.address || "",
      date: fmtDate(order?.dropoff_date) || "",
      time_slot: order?.dropoff_time_slot || order?.dropoff?.time_slot || "",
    });
  }, [order]);

  const findAddressById = React.useCallback(
    (addressId) => addresses.find((entry) => String(entry.address_id) === String(addressId)),
    [addresses]
  );

  const findAddressByLabel = React.useCallback(
    (label) => addresses.find((entry) => formatAddressLabel(entry) === label),
    [addresses]
  );

  const pickupSelectedAddress =
    findAddressById(pickup.address_id) || findAddressByLabel(pickup.address);
  const dropoffSelectedAddress =
    findAddressById(dropoff.address_id) || findAddressByLabel(dropoff.address);

  const pickupSelectValue = pickupSelectedAddress
    ? String(pickupSelectedAddress.address_id)
    : pickup.address
      ? `custom:${pickup.address}`
      : "";
  const dropoffSelectValue = dropoffSelectedAddress
    ? String(dropoffSelectedAddress.address_id)
    : dropoff.address
      ? `custom:${dropoff.address}`
      : "";

  const pickupPhone = pickupSelectedAddress?.telephone ?? order?.pickup_telephone;
  const dropoffPhone = dropoffSelectedAddress?.telephone ?? order?.dropoff_telephone;
  const pickupMap = buildGoogleMapsUrl(
    pickupSelectedAddress?.lat ?? order?.pickup_lat,
    pickupSelectedAddress?.lng ?? order?.pickup_lng
  );
  const dropoffMap = buildGoogleMapsUrl(
    dropoffSelectedAddress?.lat ?? order?.dropoff_lat,
    dropoffSelectedAddress?.lng ?? order?.dropoff_lng
  );

  const savePickup = useMutation({
    mutationFn: () =>
      api.post(`/order/${id}/pickup-address`, {
        ...pickup,
        address_id: pickup.address_id ? Number(pickup.address_id) : null,
      }),
    onSuccess: () => {
      // optimistic local update: also patch cache so `order` updates immediately
      qc.setQueryData(orderKeys.one(id), (old) =>
        old
          ? {
              ...old,
              pick_up_address_id: pickupSelectedAddress?.address_id ?? 0,
              pickup_address: pickupSelectedAddress ? formatAddressLabel(pickupSelectedAddress) : pickup.address,
              pickup_date: pickup.date,
              pickup_time_slot: pickup.time_slot,
              pickup_telephone: pickupSelectedAddress?.telephone ?? old.pickup_telephone,
              pickup_lat: pickupSelectedAddress?.lat ?? old.pickup_lat,
              pickup_lng: pickupSelectedAddress?.lng ?? old.pickup_lng,
            }
          : old
      );
      invalidate();
      setEditPickup(false);
    },
  });

  const saveDropoff = useMutation({
    mutationFn: () =>
      api.post(`/order/${id}/dropoff-address`, {
        ...dropoff,
        address_id: dropoff.address_id ? Number(dropoff.address_id) : null,
      }),
    onSuccess: () => {
      qc.setQueryData(orderKeys.one(id), (old) =>
        old
          ? {
              ...old,
              drop_off_address_id: dropoffSelectedAddress?.address_id ?? 0,
              dropoff_address: dropoffSelectedAddress ? formatAddressLabel(dropoffSelectedAddress) : dropoff.address,
              dropoff_date: dropoff.date,
              dropoff_time_slot: dropoff.time_slot,
              dropoff_telephone: dropoffSelectedAddress?.telephone ?? old.dropoff_telephone,
              dropoff_lat: dropoffSelectedAddress?.lat ?? old.dropoff_lat,
              dropoff_lng: dropoffSelectedAddress?.lng ?? old.dropoff_lng,
            }
          : old
      );
      invalidate();
      setEditDropoff(false);
    },
  });

  return (
    <div className="oa-card">
      <div className="oa-grid">
        {/* ============= PICK-UP ============= */}
        <div className="oa-box">
          <div className="oa-box-header">
            <div className="oa-box-title">Pick-Up Address</div>
            <button
              className="oa-link"
              onClick={() => setEditPickup((v) => !v)}
            >
              {editPickup ? "Cancel" : "Change"}
            </button>
          </div>

          {!editPickup ? (
            <div className="oa-readable">
              {pickupPhone && <div><strong>Phone:</strong> {pickupPhone}</div>}
              {pickup.address && <div>{pickup.address}</div>}
              {pickup.date && <div className="oa-blue">{pickup.date}</div>}
              {pickup.time_slot && (
                <div className="oa-blue">{pickup.time_slot}</div>
              )}
              {pickupMap && (
                <a
                  className="oa-map"
                  href={pickupMap}
                  target="_blank"
                  rel="noreferrer"
                >
                  {pickupMap}
                </a>
              )}
            </div>
          ) : (
            <>
              <div className="oa-row">
                <div className="oa-label">Address</div>
                <div className="oa-value">
                  <select
                    className="oa-select"
                    value={pickupSelectValue}
                    onChange={(e) => {
                      const selected = findAddressById(e.target.value);
                      setPickup((p) => ({
                        ...p,
                        address_id: selected ? String(selected.address_id) : "",
                        address: selected ? formatAddressLabel(selected) : p.address,
                      }));
                    }}
                  >
                    <option value="">-- Select Address --</option>
                    {addresses.map((a) => {
                      const fullAddress = formatAddressLabel(a);
                      return (
                        <option key={a.address_id} value={a.address_id}>
                          {fullAddress}
                        </option>
                      );
                    })}
                    {pickup.address && !pickupSelectedAddress && (
                      <option value={`custom:${pickup.address}`}>{pickup.address}</option>
                    )}
                  </select>
                </div>
              </div>

              <div className="oa-row">
                <div className="oa-label">Date</div>
                <div className="oa-value">
                  <input
                    type="date"
                    className="oa-input"
                    value={pickup.date}
                    onChange={(e) =>
                      setPickup((p) => ({ ...p, date: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="oa-row">
                <div className="oa-label">Time Slot</div>
                <div className="oa-value">
                  <SlotSelect
                    value={pickup.time_slot}
                    options={order?.pickup_slots || []}
                    onChange={(v) =>
                      setPickup((p) => ({ ...p, time_slot: v }))
                    }
                  />
                  <SlotOrderCount date={pickup.date} timeSlot={pickup.time_slot} />
                </div>
              </div>

              <div className="oa-actions">
                <button
                  className="oa-btn"
                  onClick={() => savePickup.mutate()}
                  disabled={savePickup.isLoading}
                >
                  {savePickup.isLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ============= DROP-OFF ============= */}
        <div className="oa-box">
          <div className="oa-box-header">
            <div className="oa-box-title">Drop-Off Address</div>
            <button
              className="oa-link"
              onClick={() => setEditDropoff((v) => !v)}
            >
              {editDropoff ? "Cancel" : "Change"}
            </button>
          </div>

          {!editDropoff ? (
            <div className="oa-readable">
              {dropoffPhone && <div><strong>Phone:</strong> {dropoffPhone}</div>}
              {dropoff.address && <div>{dropoff.address}</div>}
              {dropoff.date && <div className="oa-blue">{dropoff.date}</div>}
              {dropoff.time_slot && (
                <div className="oa-blue">{dropoff.time_slot}</div>
              )}
              {dropoffMap && (
                <a
                  className="oa-map"
                  href={dropoffMap}
                  target="_blank"
                  rel="noreferrer"
                >
                  {dropoffMap}
                </a>
              )}
            </div>
          ) : (
            <>
              <div className="oa-row">
                <div className="oa-label">Address</div>
                <div className="oa-value">
                  <select
                    className="oa-select"
                    value={dropoffSelectValue}
                    onChange={(e) => {
                      const selected = findAddressById(e.target.value);
                      setDropoff((d) => ({
                        ...d,
                        address_id: selected ? String(selected.address_id) : "",
                        address: selected ? formatAddressLabel(selected) : d.address,
                      }));
                    }}
                  >
                    <option value="">-- Select Address --</option>
                    {addresses.map((a) => {
                      const fullAddress = formatAddressLabel(a);
                      return (
                        <option key={a.address_id} value={a.address_id}>
                          {fullAddress}
                        </option>
                      );
                    })}
                    {dropoff.address && !dropoffSelectedAddress && (
                      <option value={`custom:${dropoff.address}`}>{dropoff.address}</option>
                    )}
                  </select>
                </div>
              </div>

              <div className="oa-row">
                <div className="oa-label">Date</div>
                <div className="oa-value">
                  <input
                    type="date"
                    className="oa-input"
                    value={dropoff.date}
                    onChange={(e) =>
                      setDropoff((d) => ({ ...d, date: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="oa-row">
                <div className="oa-label">Time Slot</div>
                <div className="oa-value">
                  <SlotSelect
                    value={dropoff.time_slot}
                    options={order?.dropoff_slots || []}
                    onChange={(v) =>
                      setDropoff((d) => ({ ...d, time_slot: v }))
                    }
                  />
                  <SlotOrderCount date={dropoff.date} timeSlot={dropoff.time_slot} />
                </div>
              </div>

              <div className="oa-actions">
                <button
                  className="oa-btn"
                  onClick={() => saveDropoff.mutate()}
                  disabled={saveDropoff.isLoading}
                >
                  {saveDropoff.isLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

